<template>
  <main class="blog-section pt-9 pb-24 md:pb-42 lg:pb-[168px]">
    <slot name="title" />
    <div
      class="container-blog mx-auto min-h-[1200px] w-[80%] min-w-[300px] max-w-[1300px]"
    >
      <div
        class="blog-top-bar mb-4 lg:mt-[100px] lg:flex lg:items-start lg:rounded-lg lg:px-9 lg:py-[18px]"
      >
        <div class="mb-4 lg:mb-0 lg:flex-1">
          <FilterBlog v-once :filters="categories" />
        </div>
        <div class="blog-search-input lg:w-80">
          <Input
            id="blog-search"
            v-model="searchInput"
            name="search"
            type="search"
            class="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm shadow-sm placeholder:text-zinc-500 focus-visible:border-landing-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-landing-primary"
            :placeholder="t('common.search')"
          />
        </div>
      </div>

      <h2 v-if="route.query.search" class="mb-10">
        <Translate
          translate-key="blog.found_articles_query"
          :translate-options="{
            countArticles: countArticles,
            query: route.query.search,
          }"
        />
      </h2>
      <h2 v-if="!posts?.length && !loading" class="mb-10">
        <Translate translate-key="blog.articles_not_found" />
      </h2>

      <ClientOnly>
        <Pagination
          v-if="countArticles > BLOG_ITEMS_PER_PAGE"
          :key="`top-${currentPage}`"
          :page="currentPage"
          :total="countArticles"
          :items-per-page="BLOG_ITEMS_PER_PAGE"
          class="mb-5"
          aria-label="Blog pagination (top)"
          @update:page="goToPage"
        >
          <nav tabindex="-1" class="blog-pagination outline-none">
            <PaginationContent class="flex flex-wrap items-center gap-1">
              <PaginationPrevious />
              <template
                v-for="(p, index) in blogPageNumbers"
                :key="`top-${p === 'ellipsis' ? `ellipsis-${index}` : p}`"
              >
                <PaginationItem v-if="p !== 'ellipsis'" :value="p">
                  {{ p }}
                </PaginationItem>
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </nav>
        </Pagination>
        <template #fallback>
          <div class="mb-5 flex justify-center" aria-hidden="true">
            <nav
              class="blog-pagination flex flex-wrap items-center gap-1"
              aria-hidden="true"
            >
              <span class="inline-block h-9 w-9" />
            </nav>
          </div>
        </template>
      </ClientOnly>

      <ul
        class="blog-card-list grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-8"
      >
        <template v-if="loading">
          <BlogCardSkeleton v-for="n in BLOG_ITEMS_PER_PAGE" :key="n" />
        </template>
        <template v-else>
          <BlogCard
            v-for="post in posts"
            :key="post.id"
            :img-main="getAbsoluteUrl(post.img_main)"
            :title="post.title"
            :author-img="getAbsoluteUrl(post.author_image)"
            :author-name="post.author_name"
            :tag-obj="post.category"
            :link="post.slug"
          />
        </template>
      </ul>

      <ClientOnly>
        <Pagination
          v-if="countArticles > BLOG_ITEMS_PER_PAGE"
          :key="`bottom-${currentPage}`"
          :page="currentPage"
          :total="countArticles"
          :items-per-page="BLOG_ITEMS_PER_PAGE"
          class="mt-5"
          aria-label="Blog pagination (bottom)"
          @update:page="goToPage"
        >
          <nav
            ref="paginationNavRef"
            tabindex="-1"
            class="blog-pagination outline-none"
          >
            <PaginationContent class="flex flex-wrap items-center gap-1">
              <PaginationPrevious />
              <template
                v-for="(p, index) in blogPageNumbers"
                :key="`bottom-${p === 'ellipsis' ? `ellipsis-${index}` : p}`"
              >
                <PaginationItem v-if="p !== 'ellipsis'" :value="p">
                  {{ p }}
                </PaginationItem>
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </nav>
        </Pagination>
        <template #fallback>
          <div class="mt-5 flex justify-center" aria-hidden="true">
            <nav
              class="blog-pagination flex flex-wrap items-center gap-1"
              aria-hidden="true"
            >
              <span class="inline-block h-9 w-9" />
            </nav>
          </div>
        </template>
      </ClientOnly>
    </div>
  </main>
</template>

<script setup lang="ts">
import type { BlogPostListItem, BlogCategory } from "~/slices/blog/types";
import { Input } from "~/shared/components/ui/input";
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import FilterBlog from "~/slices/blog/components/FilterBlog.vue";
import BlogCard from "~/slices/blog/components/BlogCard.vue";
import BlogCardSkeleton from "~/slices/blog/components/BlogCardSkeleton.vue";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "~/shared/components/ui/pagination";
import { useDebounceFn, useMediaQuery } from "@vueuse/core";
import { useGetAbsoluteUrl } from "~/shared/composables/useGetAbsoluteUrl";
import { BLOG_ITEMS_PER_PAGE } from "~/slices/blog/composables/queries";

const SEARCH_DEBOUNCE_MS = 300;

const props = defineProps<{
  posts: BlogPostListItem[];
  categories: BlogCategory[];
  loading: boolean;
  countArticles: number;
  currentPage: number;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { getAbsoluteUrl } = useGetAbsoluteUrl();

const searchInput = ref("");
/** Skip next route->input sync so our own debounced push doesn't overwrite typing */
const skipRouteSync = ref(false);
const paginationNavRef = ref<HTMLElement | null>(null);

function pageLink(page: number) {
  const path =
    page === 1 ? localePath("/blog") : localePath(`/blog/page/${page}`);
  return { path, query: route.query };
}

function goToPage(page: number) {
  router.push(pageLink(page));
  nextTick(() => {
    paginationNavRef.value?.focus({ preventScroll: true });
  });
}

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.countArticles / BLOG_ITEMS_PER_PAGE)),
);

const isMounted = ref(false);
const isMobilePagination = useMediaQuery("(max-width: 767px)");
/** Use mobile pagination only after mount to avoid SSR/client hydration mismatch */
const useMobilePagination = computed(
  () => isMounted.value && isMobilePagination.value,
);

onMounted(() => {
  isMounted.value = true;
});

/** Desktop: first 3, … current±1 …, last 3. Mobile: max 5 items — 1, …, current, …, total */
const blogPageNumbers = computed(() => {
  const total = totalPages.value;
  const current = props.currentPage;

  if (useMobilePagination.value) {
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 1 || current >= total) {
      return [1, "ellipsis", total] as (number | "ellipsis")[];
    }
    return [1, "ellipsis", current, "ellipsis", total] as (
      | number
      | "ellipsis"
    )[];
  }

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set<number>(
    [
      1,
      2,
      3,
      Math.max(1, current - 1),
      current,
      Math.min(total, current + 1),
      total - 2,
      total - 1,
      total,
    ].filter((p) => p >= 1 && p <= total),
  );
  const sorted = [...pages].sort((a, b) => a - b);
  const result: (number | "ellipsis")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i]! - sorted[i - 1]! > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]!);
  }
  return result;
});

// Sync URL -> input only when route changed from outside (initial load, browser back)
watch(
  () => route.query.search,
  (q) => {
    if (skipRouteSync.value) {
      skipRouteSync.value = false;
      return;
    }
    const val = q ? String(q) : "";
    if (searchInput.value !== val) searchInput.value = val;
  },
  { immediate: true },
);

const applySearchToRoute = useDebounceFn((val: string) => {
  skipRouteSync.value = true;
  router.push({
    path: route.path,
    query: { ...route.query, search: val.trim() || undefined },
  });
}, SEARCH_DEBOUNCE_MS);

watch(searchInput, (val) => {
  const current = (route.query.search as string) || "";
  if (val === current) return;
  applySearchToRoute(val);
});
</script>

<style scoped>
.blog-search-input :deep(input[type="search"]::-webkit-search-cancel-button) {
  cursor: pointer;
  opacity: 0.5;
}
.blog-search-input
  :deep(input[type="search"]:hover::-webkit-search-cancel-button),
.blog-search-input
  :deep(input[type="search"]:focus-within::-webkit-search-cancel-button) {
  opacity: 1;
}

.blog-pagination :deep(button:focus-visible) {
  @apply outline-none ring-2 ring-zinc-950 ring-offset-2;
}

@media (min-width: 1200px) {
  .container-blog {
    width: 90%;
  }
}
</style>
